import { useTranslation } from "react-i18next";
import { ArrowLeft, SquarePen } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  type KeyboardEvent,
} from "react";
import toast from "react-hot-toast";

import {
  AIAssistantContext,
  type IAIAssistantContext,
  type ResponseMessageTypes,
} from "@/components/ai-assistant/context";
import { ConversationsHistory } from "@/components/ai-assistant/conversation-history";
import { Button } from "@/components/ui/button";

import { MAYA_CHATS_VIEW_ID } from "../constants";
import { useChat } from "../context/use-chat";
import { useMayaSpace } from "../context/use-maya-space";

const noop = () => {};

interface ChatsViewProps {
  /** Back to the conversation (also on Escape). */
  onClose: () => void;
  /** A chat was picked or started — the conversation view takes over. */
  onOpened: () => void;
}

/**
 * The sidebar's chat switcher: the full AI Assistant page's conversation list
 * (`ConversationsHistory`) — the current space's chats, with the same search,
 * pin, rename, share and delete as that space's page — driven by a
 * sidebar-specific context so picking a chat opens it here instead of
 * navigating.
 *
 * Switching is refused while Maya is still replying — the reply would
 * otherwise land in whichever chat was opened next.
 */
export const ChatsView = ({ onClose, onOpened }: ChatsViewProps) => {
  const { t } = useTranslation(["common"]);
  const { sessionId, isTyping, loadConversation, resetChat } = useChat();
  const { history } = useMayaSpace();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const responseMessageTypeRef = useRef<ResponseMessageTypes>("initial");
  const headingId = useId();

  // Straight into the search field.
  useEffect(() => {
    rootRef.current?.querySelector<HTMLInputElement>("input")?.focus();
  }, []);

  const open = useCallback(
    async (sid: string, messageId?: number) => {
      if (isTyping) {
        toast(t("common:maya.wait_to_switch"), { id: "maya-switch" });
        return;
      }
      onOpened();
      await loadConversation(sid, messageId);
    },
    [isTyping, loadConversation, onOpened, t],
  );

  const startNew = useCallback(() => {
    if (isTyping) return;
    resetChat();
    onOpened();
  }, [isTyping, resetChat, onOpened]);

  const context = useMemo<IAIAssistantContext>(
    () => ({
      onSelectChat: (sid) => open(sid),
      // A prompt inside a chat opens that chat at that turn, highlighted.
      onSelectQuery: (sid, messageId) => open(sid, messageId),
      // The list calls this only when the chat being deleted is the open one.
      startNewChat: resetChat,
      // The rail never collapses here, and temporary chat is switched
      // from the header menu rather than the list.
      isSidebarOpen: true,
      setIsSidebarOpen: noop,
      isTempChat: false,
      startTempChat: noop,
      stopTempChat: noop,
      responseMessageTypeRef,
    }),
    [open, resetChat],
  );

  // The list doesn't need to redraw for every status line streamed into the
  // chat context — only when the open chat changes.
  const list = useMemo(
    () => (
      <ConversationsHistory
        api={history}
        activeSessionId={sessionId ?? undefined}
      />
    ),
    [history, sessionId],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Escape steps back to the conversation instead of closing the sidebar.
    // Dialogs opened from the list (rename, share) and a filled search field
    // handle their own Escape first and mark it handled.
    if (e.key !== "Escape" || e.defaultPrevented) return;
    e.preventDefault();
    onClose();
  };

  return (
    <div
      ref={rootRef}
      id={MAYA_CHATS_VIEW_ID}
      role="region"
      aria-labelledby={headingId}
      onKeyDown={handleKeyDown}
      className="animate-rise-in flex min-h-0 flex-1 flex-col"
    >
      <div className="flex items-center gap-2 px-3 pb-2">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={t("common:maya.back_to_chat")}
          title={t("common:maya.back_to_chat")}
          className="rounded-full text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="rtl-flip size-4" />
        </Button>
        <h2
          id={headingId}
          className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground"
        >
          {t("common:maya.chats")}
        </h2>
        <Button
          type="button"
          variant="outline-primary"
          size="xs"
          onClick={startNew}
          disabled={isTyping}
        >
          <SquarePen className="size-3.5" />
          {t("common:a11y.new_chat")}
        </Button>
      </div>

      {isTyping && (
        <p className="mx-3 mb-2 rounded-xs bg-warning-muted px-2.5 py-1.5 text-[11px] text-foreground">
          {t("common:maya.wait_to_switch")}
        </p>
      )}

      <AIAssistantContext.Provider value={context}>
        <div className="flex min-h-0 flex-1 flex-col px-3 pb-3">{list}</div>
      </AIAssistantContext.Provider>
    </div>
  );
};
